import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { FaEye, FaEyeSlash, FaChartBar } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';

import LoadingAnimation from '../../anims/LoadingAnimation';
import ModalProductoPadre from '../../modals/ModalProductoPadre';
import ProductCard from './ProductCard';
import Pagination from './Paginacion';
import CustomSwitch from '../../customSwitch';
import ProductTable from './ProductTable';
import ImpresionEtiqueta from './ImprimirEtiqueta';
import { getStatusClasses } from '../../../utils/LabelColors';
import WhatsAppShareModal from '../../Dashboard/Welcome/WhatsAppShareModal';
import formatAplicaciones from '../../../utils/formatAplicaciones';
import BuscadorResultFilters, { DEFAULT_RESULT_FILTERS, DEFAULT_PRECIO_MIN, DEFAULT_PRECIO_MAX } from './BuscadorResultFilters';
import BuscadorSearchForm from './BuscadorSearchForm';
import BuscadorAnalyticsModal from './BuscadorAnalyticsModal';
import { saveRecentSearch } from './buscadorRecentSearches';
import { buildCompactSearchLabel, resolveCategoryLabel } from './buscadorSearchDisplay';
import { parseBuscadorUrl, buildBuscadorUrlParams, normalizeResultFilters, normalizeFiltersForSearchPayload, enrichFiltersForFormDisplay, getActivePriceFiltersForPayload, getActiveAgotadoForPayload } from './buscadorUrlState';

/**
 * Componente reutilizable y mejorado para buscar productos con vista de tarjetas.
 * @param {string} token - Token de autenticación.
 * @param {boolean} [showActions=true] - Muestra/oculta información y acciones avanzadas (existencias, precios, etc.).
 * @param {boolean} [showPrintLabel=false] - Muestra/oculta el botón para imprimir etiquetas.
 * @param {function} [onProductSelect] - Callback que se ejecuta al seleccionar un producto. Si se proporciona, muestra un botón de "Usar". Recibe el objeto producto completo.
 * @param {boolean} [showMeliLink=true] - Muestra/oculta enlace a Mercado Libre.
 * @param {boolean} [showAnalytics=true] - Muestra/oculta el botón de estadísticas de búsqueda.
 * @param {boolean} [syncUrl=false] - Sincroniza filtros y resultados con la URL (página dedicada).
 */
const ProductSearchComponent = ({ 
  token, 
  showActions = true, 
  showPrintLabel = false, 
  onProductSelect, 
  gridColumns = 3, 
  showPriceOnly = false,
  showStoreLink = true,
  showWhatsAppShare = true,
  showMeliLink = true,
  showAnalytics = true,
  syncUrl = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    sku: '',
    nombre_producto: '',
    medidas: '',
    codigo_barras: '',
    id_vehiculo: '',
    vehiculo: '',
    nombre_marca: '',
    id_categoria: '',
    homonimos: '',
    nombre_producto_not_like: '',
    estado_rotacion: '',
  });

  // Estado para atributos dinámicos
  const [atributosDisponibles, setAtributosDisponibles] = useState([]);
  const [filtrosAtributos, setFiltrosAtributos] = useState({});

  const [vehiculos, setVehiculos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [productos, setProductos] = useState([]); // Esta es la lista "cruda" de la API
  const [currentPage, setCurrentPage] = useState(1);
  // const [productosPorPagina] = useState(18); // ELIMINADO: Se maneja dinámicamente
  const [searchClicked, setSearchClicked] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'nombre_producto', direction: 'asc' });
  const [message, setMessage] = useState('');
  const nombreProductoRef = useRef(null);
  const resultsRef = useRef(null);
  const formRef = useRef(null);
  const pendingAttributeApply = useRef(null);
  const urlBootstrappedRef = useRef(false);
  const preserveAtributosRef = useRef(false);
  const pendingUrlSearchRef = useRef(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [isModalPadreOpen, setIsModalPadreOpen] = useState(false);
  const [productoPadre, setProductoPadre] = useState(null);
  const [conteoOnly, setConteoOnly] = useState(null);
  const [tiene_imagenes, setTieneImagenes] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [productoParaImprimir, setProductoParaImprimir] = useState(null);
  const [viewMode, setViewMode] = useState('card');
  const [shareProduct, setShareProduct] = useState(null);
  const [mostrarInfoInterna, setMostrarInfoInterna] = useState(false);
  const [mostrarMetricasVentas, setMostrarMetricasVentas] = useState(false);
  const [resultFilters, setResultFilters] = useState({ ...DEFAULT_RESULT_FILTERS });
  const [isRefetching, setIsRefetching] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  useEffect(() => {
    const fetchVehiculosYCategorias = async () => {
      try {
        const requestHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        const [vehiculosResponse, categoriasResponse] = await Promise.all([
          axios.get('/api/productos/vehiculos', { headers: requestHeaders }),
          axios.get('/api/productos/categorias', { headers: requestHeaders }),
        ]);
        setVehiculos(vehiculosResponse.data);
        setCategorias(categoriasResponse.data);
      } catch (error) {
        console.error('Error al cargar vehículos o categorías:', error);
      }
    };
    fetchVehiculosYCategorias();
  }, [token]);

  useEffect(() => {
    setCurrentPage(1);
  }, [conteoOnly, tiene_imagenes, viewMode]);

  useEffect(() => {
    if (!searchClicked) {
      setShowStickyBar(false);
      return undefined;
    }
    const onScroll = () => {
      if (!resultsRef.current) return;
      const top = resultsRef.current.getBoundingClientRect().top;
      setShowStickyBar(top < 72);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [searchClicked]);

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      border: '2px solid #ffc240',
      borderRadius: '0.75rem',
      padding: '0.15rem',
      backgroundColor: 'white',
      fontWeight: 'bold',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#ffc240' : state.isFocused ? '#FDE68A' : 'white',
      color: state.isSelected ? 'black' : 'inherit',
    }),
  };

  const buildCategoryTree = (categorias) => {
    // ... (Función sin cambios)
    const groupedByParent = categorias.reduce((acc, cat) => {
      const parentId = cat.id_categoria_padre || 0;
      if (!acc[parentId]) acc[parentId] = [];
      acc[parentId].push(cat);
      return acc;
    }, {});
    Object.keys(groupedByParent).forEach(parentId => {
      groupedByParent[parentId].sort((a, b) => a.nombre_categoria.localeCompare(b.nombre_categoria));
    });
    const nestCategories = (parentId, prefix = "") => {
      const children = groupedByParent[parentId] || [];
      return children.flatMap(child => {
        const item = { ...child, displayName: prefix + child.nombre_categoria };
        const subCats = nestCategories(child.id_categoria, prefix + "↳ ");
        return [item, ...subCats];
      });
    };
    return nestCategories(0);
  };

  const categoryOptions = useMemo(() => buildCategoryTree(categorias).map((cat) => ({ value: cat.id_categoria, label: cat.displayName })), [categorias]);

  const syncSearchToUrl = useCallback((activeFilters, activeAtributos, activeResultFilters) => {
    if (!syncUrl) return;
    setSearchParams(buildBuscadorUrlParams(activeFilters, activeAtributos, activeResultFilters), { replace: true });
  }, [syncUrl, setSearchParams]);

  useEffect(() => {
    if (!syncUrl || urlBootstrappedRef.current) return;
    const parsed = parseBuscadorUrl(searchParams);
    urlBootstrappedRef.current = true;
    if (!parsed.hasActiveSearch) return;

    preserveAtributosRef.current = Object.keys(parsed.filtrosAtributos).length > 0;
    pendingUrlSearchRef.current = parsed;
    setFilters(enrichFiltersForFormDisplay(parsed.filters, vehiculos));
    setResultFilters(parsed.resultFilters);
    setFiltrosAtributos(parsed.filtrosAtributos);
  }, [syncUrl, searchParams, vehiculos]);

  useEffect(() => {
    if (!vehiculos.length) return;
    setFilters((prev) => {
      const enriched = enrichFiltersForFormDisplay(prev, vehiculos);
      return enriched.vehiculo === prev.vehiculo ? prev : enriched;
    });
  }, [vehiculos]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (selectedOption) => {
    setFilters((prev) => ({ ...prev, id_categoria: selectedOption ? selectedOption.value : '' }));
  };

  const handleVehiculoChange = (e) => {
    const vehiculoSeleccionado = vehiculos.find(v => v.vehiculo_completo === e.target.value);
    setFilters({ ...filters, vehiculo: e.target.value, id_vehiculo: vehiculoSeleccionado ? vehiculoSeleccionado.id_vehiculo : '' });
  };

  // Efecto para cargar atributos cuando cambia la categoría
  useEffect(() => {
    const fetchAtributos = async () => {
      if (!filters.id_categoria) {
        setAtributosDisponibles([]);
        if (!preserveAtributosRef.current) setFiltrosAtributos({});
        preserveAtributosRef.current = false;
        return;
      }

      try {
        const response = await axios.get(`/api/productos/atributos/categoria/${filters.id_categoria}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setAtributosDisponibles(response.data);
        if (pendingAttributeApply.current) {
          const { attrId, value } = pendingAttributeApply.current;
          pendingAttributeApply.current = null;
          setFiltrosAtributos({ [attrId]: value });
        } else if (preserveAtributosRef.current) {
          preserveAtributosRef.current = false;
        } else {
          setFiltrosAtributos({});
        }
      } catch (error) {
        console.error('Error al obtener atributos:', error);
        setAtributosDisponibles([]);
      }
    };

    fetchAtributos();
  }, [filters.id_categoria, token]);

  const handleAtributoChange = (idAtributo, valor) => {
    setFiltrosAtributos(prev => ({
      ...prev,
      [idAtributo]: valor
    }));
  };

  const handleClearFilter = (key, isAttr = false) => {
    if (isAttr) {
      setFiltrosAtributos((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    if (key === 'id_categoria') {
      setFilters((prev) => ({ ...prev, id_categoria: '' }));
      setFiltrosAtributos({});
      setAtributosDisponibles([]);
      return;
    }
    if (key === 'vehiculo') {
      setFilters((prev) => ({ ...prev, vehiculo: '', id_vehiculo: '' }));
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: '' }));
  };

  const handleApplyRecentSearch = (entry) => {
    const normalized = normalizeFiltersForSearchPayload(entry.filters || {});
    setFilters(enrichFiltersForFormDisplay(normalized, vehiculos));
    setFiltrosAtributos({ ...(entry.filtrosAtributos || {}) });
  };

  const resolveVehiculoApply = (value, rawValue) => {
    const applyValue = rawValue ?? value;
    const displayName = value || applyValue;
    const isNumericId = /^\d+$/.test(String(applyValue));
    const vehiculoSeleccionado = vehiculos.find(
      (v) => (isNumericId && String(v.id_vehiculo) === String(applyValue))
        || v.vehiculo_completo === displayName
    );
    return {
      vehiculo: vehiculoSeleccionado?.vehiculo_completo || displayName,
      id_vehiculo: vehiculoSeleccionado ? vehiculoSeleccionado.id_vehiculo : '',
    };
  };

  const handleFillFromAnalytics = (field, value) => {
    if (field === 'vehiculo') {
      const resolved = resolveVehiculoApply(value, value);
      setFilters((prev) => ({
        ...prev,
        vehiculo: resolved.vehiculo,
        id_vehiculo: resolved.id_vehiculo,
      }));
      return;
    }
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyQuery = (filtersArray) => {
    if (!Array.isArray(filtersArray) || filtersArray.length === 0) return;
    const next = { ...filters };
    filtersArray.forEach(({ key, value, rawValue }) => {
      const applyValue = rawValue ?? value;
      if (key === 'vehiculo') {
        const resolved = resolveVehiculoApply(value, rawValue);
        next.vehiculo = resolved.vehiculo;
        next.id_vehiculo = resolved.id_vehiculo;
      } else if (key === 'id_categoria') {
        next.id_categoria = applyValue;
      } else if (key in next) {
        next[key] = applyValue;
      }
    });
    setFilters(next);
  };

  const handleApplyAttribute = (categoryId, attrId, value) => {
    if (!categoryId || !attrId) return;
    if (String(filters.id_categoria) === String(categoryId)) {
      setFiltrosAtributos((prev) => ({ ...prev, [attrId]: value }));
      return;
    }
    pendingAttributeApply.current = { attrId, value };
    setFilters((prev) => ({ ...prev, id_categoria: categoryId }));
  };

  const hasActiveFilters = Object.values(filters).some((v) => String(v).trim() !== '');

  const searchSummary = useMemo(() => {
    const categoryLabel = resolveCategoryLabel(filters.id_categoria, categoryOptions);
    return buildCompactSearchLabel({
      filters,
      filtrosAtributos,
      categoryLabel,
    });
  }, [filters, filtrosAtributos, categoryOptions]);

  const handleReset = () => {
    setFilters({
      sku: '',
      nombre_producto: '',
      medidas: '',
      codigo_barras: '',
      vehiculo: '',
      id_vehiculo: '',
      nombre_marca: '',
      id_categoria: '',
      homonimos: '',
      nombre_producto_not_like: '',
      estado_rotacion: ''
    });
    setFiltrosAtributos({});
    setAtributosDisponibles([]);
    setProductos([]);
    setSearchClicked(false);
    setMessage('');
    setResultFilters({ ...DEFAULT_RESULT_FILTERS });
    setIsRefetching(false);
    if (syncUrl) {
      setSearchParams({}, { replace: true });
    }
  };

  const executeSearch = async ({
    isRefetch = false,
    resultFiltersOverride = null,
    filtersOverride = null,
    filtrosAtributosOverride = null,
    skipUrlSync = false,
  } = {}) => {
    const activeFilters = filtersOverride ?? filters;
    const activeAtributos = filtrosAtributosOverride ?? filtrosAtributos;
    const activeResultFilters = normalizeResultFilters(resultFiltersOverride ?? resultFilters);
    const apiEndpoint = '/api/productos/buscar';
    const requestHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    if (isRefetch) {
      setIsRefetching(true);
    } else {
      setSearchClicked(true);
      setIsLoading(true);
      setConteoOnly(null);
      setTieneImagenes(null);
    }

    try {
      const params = { guardarDb: 'true', search_source: 'employee_buscador' };
      const payload = {
        ...normalizeFiltersForSearchPayload(activeFilters),
        atributos: activeAtributos,
        sort: activeResultFilters.sort,
        ...getActivePriceFiltersForPayload(activeResultFilters),
        ...getActiveAgotadoForPayload(activeResultFilters),
        ...(activeFilters.estado_rotacion ? { estado_rotacion: activeFilters.estado_rotacion } : {})
      };
      const response = await axios.post(apiEndpoint, payload, { headers: requestHeaders, params });
      if (response.data.message) {
        const hasResultRefinements =
          Object.keys(getActivePriceFiltersForPayload(activeResultFilters)).length > 0
          || Object.keys(getActiveAgotadoForPayload(activeResultFilters)).length > 0
          || (activeResultFilters.sort && activeResultFilters.sort !== DEFAULT_RESULT_FILTERS.sort);
        setMessage(hasResultRefinements ? '' : response.data.message);
        setProductos([]);
      } else {
        setMessage('');
        setProductos(response.data);
        if (!isRefetch) {
          const categoryLabel = resolveCategoryLabel(activeFilters.id_categoria, categoryOptions);
          saveRecentSearch(activeFilters, activeAtributos, {
            label: buildCompactSearchLabel({ filters: activeFilters, filtrosAtributos: activeAtributos, categoryLabel }),
          });
        }
      }
      if (!skipUrlSync) {
        syncSearchToUrl(activeFilters, activeAtributos, activeResultFilters);
      }
      if (resultFiltersOverride != null) {
        setResultFilters(activeResultFilters);
      }
      setCurrentPage(1);
    } catch (error) {
      console.error('Error al buscar productos:', error);
      toast.error('Ocurrió un error al buscar.');
    } finally {
      if (isRefetch) {
        setIsRefetching(false);
      } else {
        setIsLoading(false);
        setTimeout(() => {
          resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  };

  useEffect(() => {
    if (!pendingUrlSearchRef.current) return undefined;
    const parsed = pendingUrlSearchRef.current;
    const needsAtributos = parsed.filters.id_categoria && Object.keys(parsed.filtrosAtributos).length > 0;
    if (needsAtributos && String(filters.id_categoria) === String(parsed.filters.id_categoria) && atributosDisponibles.length === 0) {
      return undefined;
    }
    pendingUrlSearchRef.current = null;
    executeSearch({
      filtersOverride: parsed.filters,
      filtrosAtributosOverride: parsed.filtrosAtributos,
      resultFiltersOverride: parsed.resultFilters,
    });
    return undefined;
  }, [filters.id_categoria, atributosDisponibles, filtrosAtributos]);

  const handleSearch = async (e) => {
    e.preventDefault();
    await executeSearch({ isRefetch: false });
  };

  const handleSortChange = async (sort) => {
    const next = normalizeResultFilters({ ...resultFilters, sort });
    setResultFilters(next);
    if (searchClicked) {
      await executeSearch({ isRefetch: true, resultFiltersOverride: next });
    }
  };

  const handleApplyPrice = async (priceUpdate) => {
    const next = normalizeResultFilters({ ...resultFilters, ...priceUpdate });
    setResultFilters(next);
    if (searchClicked) {
      await executeSearch({ isRefetch: true, resultFiltersOverride: next });
    }
  };

  const handleClearPriceFilter = async (which) => {
    const next = normalizeResultFilters({
      ...resultFilters,
      precioMin: which === 'min' ? DEFAULT_PRECIO_MIN : resultFilters.precioMin,
      precioMax: which === 'max' ? DEFAULT_PRECIO_MAX : resultFilters.precioMax,
    });
    setResultFilters(next);
    if (searchClicked) {
      await executeSearch({ isRefetch: true, resultFiltersOverride: next });
    }
  };

  const handleAgotadoChange = async (nextAgotado) => {
    const next = normalizeResultFilters({ ...resultFilters, agotado: nextAgotado });
    setResultFilters(next);
    if (searchClicked) {
      await executeSearch({ isRefetch: true, resultFiltersOverride: next });
    }
  };

  const handleUpdateExistencias = async (producto) => {
    // ... (Función sin cambios)
    if (!token) {
      toast.error("Debe iniciar sesión para poder actualizar la cantidad.");
      return;
    }

    const { value: nuevaCantidad } = await Swal.fire({
      title: `Actualizar stock de:`,
      html: `<b class="text-lg">${producto.nombre_producto}</b>`,
      input: 'number',
      inputLabel: 'Especifica la nueva cantidad:',
      inputValue: parseFloat(producto.existencias).toFixed(1),
      inputAttributes: {
        min: 0,
        step: 1,
      },
      showCancelButton: true,
      confirmButtonText: 'Actualizar',
      cancelButtonText: 'Cancelar',
    });

    if (nuevaCantidad !== undefined && !isNaN(nuevaCantidad)) {
      try {
        const idEmpleado = Cookies.get('id_empleado');
        await axios.put(
          '/api/productos/existencias',
          { idProducto: producto.id_producto, cantidad: nuevaCantidad, idEmpleado },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const { data: movimientoReciente } = await axios.get(
          `/api/productos/movimientos-recientes/${producto.id_producto}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // CAMBIO: Actualizamos la lista 'cruda' de productos
        // 'processedProducts' y 'sortedProducts' se recalcularán automáticamente
        setProductos((prev) =>
          prev.map((p) =>
            p.id_producto === producto.id_producto
              ? { ...p, existencias: nuevaCantidad, ...movimientoReciente }
              : p
          )
        );
        toast.success(`Existencias actualizadas.`);
      } catch (error) {
        console.error('Error al grabar el conteo:', error);
        toast.error('Error al grabar el conteo.');
      }
    }
  };

  const openModalProductoPadre = (producto) => {
    setProductoPadre(producto);
    setIsModalPadreOpen(true);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // CAMBIO PRINCIPAL: LÓGICA DE OPTIMIZACIÓN
  // Pre-procesamos la lista de productos UNA SOLA VEZ
  const processedProducts = useMemo(() => {
    if (!productos || productos.length === 0) {
      return [];
    }

    // Mapa para almacenar datos agregados de los hijos
    // Clave: id_padre, Valor: { prices: [], stock: 0 }
    const parentData = new Map();

    // 1. Primera pasada: Agregamos datos de los hijos
    for (const producto of productos) {
      if (producto.es_hijo && producto.id_padre) {
        if (!parentData.has(producto.id_padre)) {
          parentData.set(producto.id_padre, { prices: [], stock: 0 });
        }
        const data = parentData.get(producto.id_padre);
        data.prices.push(producto.precio_venta);
        data.stock += (producto.existencias || 0);
      }
    }

    // 2. Segunda pasada: Creamos la lista final de productos (solo padres/independientes)
    // y adjuntamos los datos agregados.
    const displayList = [];
    for (const producto of productos) {
      // Solo incluimos productos que NO son hijos en la lista final
      if (!producto.es_hijo) {
        let rango_precios_hijos = null;
        let total_existencias = producto.existencias; // Stock por defecto

        // Si es un padre, usamos los datos agregados
        if (producto.es_padre && parentData.has(producto.id_producto)) {
          const data = parentData.get(producto.id_producto);

          if (data.prices.length > 0) {
            const precioMin = Math.min(...data.prices);
            const precioMax = Math.max(...data.prices);
            rango_precios_hijos = { precio_min: precioMin, precio_max: precioMax };
          }

          total_existencias = data.stock; // El stock del padre es la suma de los hijos
        }

        displayList.push({
          ...producto,
          rango_precios_hijos: rango_precios_hijos,
          existencias: total_existencias, // 'existencias' ahora tiene el total correcto
        });
      }
    }
    return displayList;

  }, [productos]); // Este memo solo se re-ejecuta cuando 'productos' (de la API) cambia.


  // CAMBIO: 'sortedProducts' ahora usa 'processedProducts' (la lista optimizada)
  const sortedProducts = useMemo(() => {
    const filtered = processedProducts // <-- Usamos la lista pre-procesada
      .filter((p) => {
        // El filtro !p.es_hijo ya se aplicó en processedProducts
        const conteoFilter = conteoOnly === null || p.tieneMovimientosRecientes === conteoOnly;
        const imagenesFilter = tiene_imagenes === null || (tiene_imagenes ? p.id_imagen !== null : p.id_imagen === null);
        return conteoFilter && imagenesFilter;
      });

    if (viewMode !== 'list') {
      return filtered;
    }

    return filtered.sort((a, b) => {
      if (!sortConfig.key) return 0;
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [processedProducts, conteoOnly, tiene_imagenes, sortConfig, viewMode]);

  const hasClientSideFilterMismatch = productos.length > 0 && sortedProducts.length === 0;
  const hasAgotadoFilterActive = resultFilters.agotado === true || resultFilters.agotado === false;
  const hasPriceOrSortFilterActive =
    (resultFilters.sort ?? DEFAULT_RESULT_FILTERS.sort) !== DEFAULT_RESULT_FILTERS.sort ||
    (resultFilters.precioMin ?? DEFAULT_PRECIO_MIN) > DEFAULT_PRECIO_MIN ||
    (resultFilters.precioMax ?? DEFAULT_PRECIO_MAX) < DEFAULT_PRECIO_MAX;

  // CAMBIO: Eliminadas las funciones ineficientes
  // const calculatePriceRange = (...) 
  // const calculateTotalStock = (...)

  const productosPorPaginaActual = viewMode === 'card' ? 18 : 10;
  const paginatedProducts = sortedProducts.slice((currentPage - 1) * productosPorPaginaActual, currentPage * productosPorPaginaActual);
  const totalPages = Math.ceil(sortedProducts.length / productosPorPaginaActual);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div>
      {isModalPadreOpen && <ModalProductoPadre productoPadre={productoPadre} onClose={() => setIsModalPadreOpen(false)} token={token} />}
      {showPrintLabel && productoParaImprimir && <ImpresionEtiqueta producto={productoParaImprimir} onClose={() => setProductoParaImprimir(null)} />}

      {showAnalytics && analyticsOpen && (
        <BuscadorAnalyticsModal
          isOpen={analyticsOpen}
          onClose={() => setAnalyticsOpen(false)}
          token={token}
          onApplyTerm={handleFillFromAnalytics}
          onApplyQuery={handleApplyQuery}
          onApplyAttribute={handleApplyAttribute}
        />
      )}

      {showStickyBar && searchClicked && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-b border-gray-200 shadow-md px-4 py-2 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-gray-500 truncate">Resultados</p>
            <p className="text-sm font-semibold text-gray-800 truncate">{searchSummary}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm font-bold text-yellow-700">{sortedProducts.length}</span>
            <button
              type="button"
              onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-yellow-100 text-gray-800"
            >
              Nueva búsqueda
            </button>
          </div>
        </div>
      )}

      <div className="animate-aurora" ref={formRef}>
        <div className="container mx-auto px-4 sm:px-8 md:px-14 py-4 pb-8">
          <div className="flex items-center justify-center gap-3 mb-6 border-b-2 border-yellow-500 pb-2">
            <h1 className="text-2xl font-bold text-white">Buscar Producto</h1>
            {showAnalytics && (
              <button
                type="button"
                onClick={() => setAnalyticsOpen(true)}
                className="inline-flex items-center justify-center rounded-xl border border-white/30 bg-white/10 p-2.5 text-white/90 transition-colors hover:bg-white/20 hover:text-white"
                title="Estadísticas de búsqueda"
                aria-label="Estadísticas de búsqueda"
              >
                <FaChartBar className="h-5 w-5" />
              </button>
            )}
          </div>
          <BuscadorSearchForm
            filters={filters}
            filtrosAtributos={filtrosAtributos}
            atributosDisponibles={atributosDisponibles}
            vehiculos={vehiculos}
            categoryOptions={categoryOptions}
            customSelectStyles={customSelectStyles}
            nombreProductoRef={nombreProductoRef}
            token={token}
            onInputChange={handleInputChange}
            onVehiculoChange={handleVehiculoChange}
            onCategoryChange={handleCategoryChange}
            onAtributoChange={handleAtributoChange}
            onClearFilter={handleClearFilter}
            onApplyRecentSearch={handleApplyRecentSearch}
            onApplyFrequentQuery={handleApplyQuery}
            onSearch={handleSearch}
            onReset={handleReset}
            hasActiveFilters={hasActiveFilters}
          />
        </div>
      </div>

      {/* ÁREA DE RESULTADOS */}
      {searchClicked && (
        <div ref={resultsRef} className='px-4 sm:px-6 lg:px-8 py-6 bg-gray-50 min-h-[50vh]'>
          {isLoading ? (<LoadingAnimation />) :
            message ? (<p className="text-red-500 font-bold mt-6 mb-8 text-center">{message}<span className='text-5xl block mt-2'>😔</span></p>) :
              (
                <>
                  {searchClicked && !message && (
                    <div className="mt-4 mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm space-y-4">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <h3 className="text-lg font-semibold text-gray-800 shrink-0">
                          {sortedProducts.length} resultado{sortedProducts.length !== 1 ? 's' : ''} encontrado{sortedProducts.length !== 1 ? 's' : ''}
                          {isRefetching && (
                            <span className="ml-2 text-sm font-normal text-yellow-600">Actualizando...</span>
                          )}
                        </h3>
                        <BuscadorResultFilters
                          resultFilters={resultFilters}
                          onSortChange={handleSortChange}
                          onApplyPrice={handleApplyPrice}
                          onClearPriceFilter={handleClearPriceFilter}
                          isRefetching={isRefetching}
                        />
                      </div>
                      {searchClicked && !message && (
                        <div className="flex flex-wrap items-center justify-start gap-4 pt-2 border-t border-gray-100">
                          <CustomSwitch label="Solo con conteo ✅" state={conteoOnly} onChange={setConteoOnly} />
                          <CustomSwitch label="Tiene Imágenes 📷" state={tiene_imagenes} onChange={setTieneImagenes} />
                          <CustomSwitch label="Agotado (sin stock)" state={resultFilters.agotado ?? null} onChange={handleAgotadoChange} />
                        <button
                          type="button"
                          onClick={() => setMostrarInfoInterna((v) => !v)}
                          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                            mostrarInfoInterna
                              ? 'text-amber-700 border-amber-200 bg-amber-50'
                              : 'text-gray-500 border-gray-200 hover:text-amber-600 hover:border-amber-200'
                          }`}
                          title={mostrarInfoInterna ? 'Ocultar proveedor en todas las tarjetas' : 'Mostrar proveedor en todas las tarjetas'}
                        >
                          {mostrarInfoInterna ? <FaEyeSlash /> : <FaEye />}
                          {mostrarInfoInterna ? 'Ocultar proveedor' : 'Mostrar proveedor'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setMostrarMetricasVentas((v) => !v)}
                          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                            mostrarMetricasVentas
                              ? 'text-emerald-700 border-emerald-200 bg-emerald-50'
                              : 'text-gray-500 border-gray-200 hover:text-emerald-600 hover:border-emerald-200'
                          }`}
                          title={mostrarMetricasVentas ? 'Ocultar métricas de ventas en todas las tarjetas' : 'Mostrar ventas 30d, vendidos y última venta'}
                        >
                          {mostrarMetricasVentas ? <FaEyeSlash /> : <FaEye />}
                          {mostrarMetricasVentas ? 'Ocultar ventas' : 'Mostrar ventas'}
                        </button>
                          <button
                            onClick={() => { setViewMode(viewMode === 'card' ? 'list' : 'card'); }}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors duration-200 shadow-md"
                            title={`Cambiar a vista de ${viewMode === 'card' ? 'Lista' : 'Tarjetas'}`}
                          >
                            Vista: {viewMode === 'card' ? 'Lista 📋' : 'Tarjetas 🎴'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {sortedProducts.length > 0 ? (
                    <div className={`relative transition-opacity duration-200 ${isRefetching ? 'opacity-50 pointer-events-none' : ''}`}>
                      {isRefetching && viewMode === 'card' && (
                        <div className="absolute inset-0 z-10 grid grid-cols-1 md:grid-cols-3 gap-4 pointer-events-none">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="h-48 rounded-2xl bg-gray-200/60 animate-pulse" />
                          ))}
                        </div>
                      )}
                      {viewMode === 'card' ? (
                        <div className={`grid grid-cols-1 md:grid-cols-${gridColumns} xl:grid-cols-${gridColumns} 2xl:grid-cols-${gridColumns >= 4 ? 4 : gridColumns} gap-6 mb-4`}>
                          {/* CAMBIO: Lógica de cálculo eliminada del map */}
                          {paginatedProducts.map((producto) => {
                            // 'producto' ya tiene 'existencias' y 'rango_precios_hijos' correctos
                            return (
                              <ProductCard
                                key={producto.id_producto}
                                producto={producto}
                                token={token}
                                showActions={showActions}
                                showPrintLabel={showPrintLabel}
                                onProductSelect={onProductSelect}
                                onUpdateStock={handleUpdateExistencias}
                                onOpenParentModal={openModalProductoPadre}
                                onPrintLabel={setProductoParaImprimir}
                                formatAplicaciones={formatAplicaciones}
                                getStatusClasses={getStatusClasses}
                                showPriceOnly={showPriceOnly}
                                showStoreLink={showStoreLink}
                                showWhatsAppShare={showWhatsAppShare}
                                showMeliLink={showMeliLink}
                                onShareWhatsApp={(p) => setShareProduct(p)}
                                mostrarInfoInterna={mostrarInfoInterna}
                                mostrarMetricasVentas={mostrarMetricasVentas}
                              />
                            );
                          })}
                        </div>
                      ) : (
                        <ProductTable
                          paginatedProducts={paginatedProducts}
                          token={token}
                          showActions={showActions}
                          showPrintLabel={showPrintLabel}
                          onProductSelect={onProductSelect}
                          onUpdateStock={handleUpdateExistencias}
                          onOpenParentModal={openModalProductoPadre}
                          onPrintLabel={setProductoParaImprimir}
                          formatAplicaciones={formatAplicaciones}
                          handleSort={handleSort}
                          showStoreLink={showStoreLink}
                          showWhatsAppShare={showWhatsAppShare}
                          showMeliLink={showMeliLink}
                          onShareWhatsApp={(p) => setShareProduct(p)}
                        />
                      )}

                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  ) : (
                    !isLoading && !message && (
                      <p className="text-center text-red-500 font-bold mt-6 mb-8">
                        {hasClientSideFilterMismatch
                          ? 'Ningún resultado coincide con los filtros de conteo o imágenes. Prueba desactivarlos.'
                          : hasAgotadoFilterActive
                            ? 'No hay productos con ese criterio de stock. Prueba otro valor del filtro Agotado.'
                          : hasPriceOrSortFilterActive
                            ? 'No hay productos en este rango de precio u orden. Ajusta el rango o el criterio de ordenación.'
                            : 'No se encontraron resultados para tu búsqueda 😔'}
                      </p>
                    )
                  )}
                </>
              )}
        </div>
      )}

      {/* Modal para compartir en WhatsApp */}
      {shareProduct && (
        <WhatsAppShareModal 
          producto={shareProduct} 
          onClose={() => setShareProduct(null)} 
          token={token} 
        />
      )}
    </div>
  );
};

export default ProductSearchComponent;